/* global fetch */

import { config, type VectorSignedPath } from '../config';
import {
  APIResponse,
  fetchAPI,
  headers,
  SpanTag,
  stleaf,
} from '../helpers';
import { isVectorTileSignatureExpiredError } from './vector-tile-auth';
import { TableColumnFilter } from './maps';

const anonFetchInit = (): RequestInit & {
  birdiSkipPublicShareHeaders?: boolean;
} => ({
  method: 'GET',
  credentials: 'omit',
  cache: 'reload',
  headers: { Accept: 'application/json, text/plain, */*' },
  birdiSkipPublicShareHeaders: true,
});

async function resolveSignedPath(
  layerId: string,
): Promise<VectorSignedPath | undefined> {
  let s = config.getVectorSignedAccess?.({ layerId });
  if (s) return s;
  await config.remintVectorSignedAccess?.(layerId);
  s = config.getVectorSignedAccess?.({ layerId });
  return s;
}

async function signedMvtFetch<T>(
  st: SpanTag,
  spanLeaf: string,
  layerId: string,
  absoluteUrl: string,
): Promise<APIResponse<T>> {
  const init = anonFetchInit();
  let res = await fetchAPI<T>(stleaf(st, spanLeaf), absoluteUrl, init);
  if (
    res.status === 403 &&
    isVectorTileSignatureExpiredError(
      res.body as Record<string, unknown> | undefined,
    )
  ) {
    await config.remintVectorSignedAccess?.(layerId);
    res = await fetchAPI<T>(stleaf(st, `${spanLeaf}r`), absoluteUrl, init);
  }
  return res;
}

export interface MvtFieldMetadata {
  field: string;
  fieldType: 'numeric' | 'color' | 'category';
  minValue: number | null;
  maxValue: number | null;
}

export interface MvtCursor {
  sortKey: number | null;
  id: string;
}

export interface MvtFeatureRow {
  id: string;
  sortKey: number | null;
  properties: Record<string, unknown>;
  geometryType: string | null;
}

export interface MvtFeaturesPage {
  features: MvtFeatureRow[];
  nextCursor: MvtCursor | null;
}

export interface MvtFieldsResponse {
  fields: MvtFieldMetadata[];
  geometryTypes: string[];
}

export interface MvtCategoriesResponse {
  categories: string[];
}

export interface ListFeaturesOptions {
  limit?: number;
  cursor?: MvtCursor | null;
  columnFilters?: TableColumnFilter[];
  search?: string | null;
}

export const getFields = (
  st: SpanTag,
  mapId: string,
  layerId: string,
  vectorId?: string | null,
): Promise<APIResponse<MvtFieldsResponse>> => {
  void mapId;
  void vectorId;
  return (async () => {
    const signed = await resolveSignedPath(layerId);
    if (!signed) {
      return { status: 503 } as APIResponse<MvtFieldsResponse>;
    }
    return signedMvtFetch<MvtFieldsResponse>(
      st,
      'mvtfld',
      layerId,
      signed.mapFeaturesUrls.fields,
    );
  })();
};

export const getFieldCategories = (
  st: SpanTag,
  mapId: string,
  layerId: string,
  field: string,
  vectorId?: string | null,
): Promise<APIResponse<MvtCategoriesResponse>> => {
  void mapId;
  void vectorId;
  return (async () => {
    const signed = await resolveSignedPath(layerId);
    if (!signed) {
      return { status: 503 } as APIResponse<MvtCategoriesResponse>;
    }
    const url = signed.mapFeaturesUrls.fieldCategoriesTemplate.replace(
      '{field}',
      encodeURIComponent(field),
    );
    return signedMvtFetch<MvtCategoriesResponse>(st, 'mvtcat', layerId, url);
  })();
};

export const listFeatures = (
  st: SpanTag,
  mapId: string,
  layerId: string,
  opts: ListFeaturesOptions = {},
  vectorId?: string | null,
): Promise<APIResponse<MvtFeaturesPage>> => {
  void mapId;
  void vectorId;
  return (async () => {
    const signed = await resolveSignedPath(layerId);
    if (!signed) {
      return { status: 503 } as APIResponse<MvtFeaturesPage>;
    }
    const params = new URLSearchParams();
    if (opts.limit != null) params.set('limit', String(opts.limit));
    if (opts.cursor?.id) {
      params.set('cursorId', opts.cursor.id);
      if (opts.cursor.sortKey != null) {
        params.set('cursorSortKey', String(opts.cursor.sortKey));
      }
    }
    if (opts.columnFilters && opts.columnFilters.length > 0) {
      params.set('filters', JSON.stringify(opts.columnFilters));
    }
    if (opts.search) params.set('search', opts.search);
    const qs = params.toString();
    const url = `${signed.mapFeaturesUrls.features}${qs ? `?${qs}` : ''}`;
    return signedMvtFetch<MvtFeaturesPage>(st, 'mvtfts', layerId, url);
  })();
};

export const countFeatures = (
  st: SpanTag,
  mapId: string,
  layerId: string,
  columnFilters: TableColumnFilter[] = [],
  search?: string | null,
  vectorId?: string | null,
): Promise<APIResponse<{ count: number }>> => {
  void mapId;
  void vectorId;
  return (async () => {
    const signed = await resolveSignedPath(layerId);
    if (!signed) {
      return { status: 503 } as APIResponse<{ count: number }>;
    }
    const params = new URLSearchParams();
    if (columnFilters.length > 0) {
      params.set('filters', JSON.stringify(columnFilters));
    }
    if (search) params.set('search', search);
    const qs = params.toString();
    const url = `${signed.mapFeaturesUrls.count}${qs ? `?${qs}` : ''}`;
    return signedMvtFetch<{ count: number }>(st, 'mvtcnt', layerId, url);
  })();
};
